import React from 'react'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { Grid, Box, Link, Tooltip } from '@material-ui/core'
import { Formik, Field } from 'formik'
import LiquidSelectAdapter from '@src/components/LiquidSelectAdapter'
import Button from '@material-ui/core/Button'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from '../../../messages'
import { useShrnaDropDownListLazyQuery } from '@src/queries/ShrnaSpecificationDetailsQuery.generated'
import { useShrnaPriceCallMutation } from '@src/mutations/SemiconfigShrnaMutation.generated'
import { semiConfigContext, ProductDetails } from '../Shrna'
import {
  SpecificationMessages,
  ActiveLinkType,
  getPhysicalMaterialKey,
} from '../Enum'
import ErrorAlert from '../../../../Configurators/components/ErrorAlert'
import { useRouter, shrnaRoute } from '@src/routes'
import { initialState } from '../Reducer'
import ValidationErrors from '../../../../Configurators/validations/ValidationErrors'
import CircleLoader from '@src/components/CircleLoader/CircleLoader'
import InfoIcon from '@material-ui/icons/Info'
import { SemiConfiguratorValues } from '../../../utils/constants'

const useStyles = makeStyles((theme: Theme) => {
  return {
    contentWrapper: {
      backgroundColor: theme.palette.common.white,
      padding: theme.spacing(8.5, 8.5, 8.5),
    },
    label: {
      fontWeight: theme.typography.fontWeightBold,
      fontSize: theme.typography.pxToRem(14),
      marginBottom: theme.spacing(0),
    },
    subHead: {
      fontSize: theme.typography.pxToRem(16),
      fontWeight: theme.typography.fontWeightBold,
      margin: theme.spacing(2, 0),
    },
    specificationsWrapper: {
      '& span.MuiFormControlLabel-label': {
        fontSize: theme.typography.pxToRem(14),
      },
    },
    radio: {
      padding: theme.spacing(1),
      color: 'rgba(0,0,0,0.1)',
    },
    linkStyle: {
      color: theme.palette.primary.main,
    },
    vectorLabel: {
      color: '#3c3c4bdb',
      fontSize: theme.typography.pxToRem(13),
    },
    vectorLabel2: {
      paddingBottom: theme.spacing(1),
    },
    nextButton: {
      marginTop: theme.spacing(4),
      minWidth: 220,
      height: theme.spacing(12),
    },
    hideRow: {
      display: 'none',
    },
    noDrop: {
      '& .MuiFormControlLabel-root': {
        cursor: 'no-drop',
      },
      '& .Mui-disabled': {
        cursor: 'no-drop',
      },
    },
    fieldLabel: {
      marginTop: theme.spacing(5),
      '& label': {
        fontWeight: theme.typography.fontWeightMedium,
        fontSize: theme.typography.pxToRem(14),
      },
      '& .MuiInput-root': {
        width: '75%',
        '& .MuiSelect-root': {
          height: 35,
          lineHeight: theme.typography.pxToRem(25),
        },
      },
    },
    fieldLabelPadding: {
      paddingLeft: theme.spacing(1.5),
    },
    fieldLabelphyMat: {
      '& label': {
        fontWeight: theme.typography.fontWeightMedium,
        fontSize: theme.typography.pxToRem(14),
      },
      '& .MuiInput-root': {
        width: '75%',
        '& .MuiSelect-root': {
          height: 35,
          lineHeight: theme.typography.pxToRem(25),
        },
      },
    },
    InfoPopup: {
      maxWidth: '260px',
      backgroundColor: theme.palette.common.white,
      position: 'relative',
      boxShadow: theme.shadows[4],
      padding: theme.spacing(5),
      fontSize: theme.typography.pxToRem(12),
      color: theme.palette.common.black,
      borderRadius: '2%',
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
    icon: {
      fontSize: theme.typography.pxToRem(16),
      verticalAlign: 'middle',
    },
    IconButton: {
      outlined: true,
      padding: theme.spacing(1),
      marginBottom: 2,
    },
    InfoIcon: {
      outlined: true,
      position: 'relative',
      fontSize: theme.typography.pxToRem(16),
      top: theme.spacing(0.5),
      marginLeft: theme.spacing(1.5),
    },
    arrow: {
      color: theme.palette.common.white,
    },
  }
})

interface SpecificationsProps {
  productDetails: ProductDetails
}

const Specifications: React.FC<SpecificationsProps> = (props) => {
  const classes = useStyles()
  const intl = useIntl()
  const { state, dispatch } = React.useContext(semiConfigContext)
  const [getShrnaDropDownList, { data, loading: dropdownload }] =
    useShrnaDropDownListLazyQuery()
  const [selectedMaterial, setSelectedMaterial] = React.useState('noSelection')
  const [validationMessage, setValidationMessage] = React.useState<string[]>([])
  const [physicalMaterialNotSelected, setPhysicalMaterialNotSelected] =
    React.useState(false)
  const message = intl.formatMessage(
    messages.CONF_SELECT_REQUIRED_FIELD_crispgrna
  )
  const [open, setOpen] = React.useState(false)
  const { shrnaDropDownResponseList } = state
  const [vectorFlag, setVectorFlag] = React.useState(false)
  const [vector, setVector] = React.useState('pLKO1')
  const [titer, setTiter] = React.useState('powerof6')
  const [volFlag, setVolFlag] = React.useState(false)
  const [volume, setVolume] = React.useState('pointTwo')
  const [titerFlag, setTiterFlag] = React.useState(false)
  const router = useRouter()
  const [loader, setLoader] = React.useState(false)

  React.useEffect(() => {
    window.scrollTo(0, 0)
    if (state.shrnaDropDownValue.physicalMaterial !== '') {
      setSelectedMaterial(state.shrnaDropDownValue.physicalMaterial)
      setPhysicalMaterialNotSelected(false)
    }
    // TODO: need a test before we refactor these dependencies
  }, [])

  const shrnaDropDownResult = async (obj): Promise<void> => {
    await getShrnaDropDownList({ variables: { input: obj } })
  }

  const [shrnaPriceCall, { loading: priceload }] = useShrnaPriceCallMutation()

  const semiShrnaPriceCall = async (obj): Promise<void> => {
    const response: any = await shrnaPriceCall({
      variables: { input: obj, productInput: props.productDetails },
    })
    if (response) {
      if (!response.data?.shrnaPriceCall?.serverError) {
        dispatch({
          type: 'UPDATE_PRICE_CALL_RESPONSE',
          data: response.data.shrnaPriceCall,
        })
        const url = `${shrnaRoute.index()}?activeLink=${
          ActiveLinkType.ReviewAndConfirm
        }`
        router.push(url)
      } else {
        dispatch({
          type: 'SET_PAGE_ERROR',
          data: SemiConfiguratorValues.InternalServerError,
        })
        window.scrollTo(0, 0)
      }
    }
  }

  const setInitialState = (): void => {
    dispatch({ type: 'TRACE_ON_CLICK', data: true })
    dispatch({
      type: 'UPDATE_PRICE_CALL_RESPONSE',
      data: initialState.priceCallResponse,
    })
  }

  const onPageLoadTiterValues = (): void => {
    if (
      state.priceCallData.physicalFormat.titer !== '' &&
      state.priceCallData.physicalFormat.titer !== undefined
    ) {
      setTiterFlag(false)
      if (
        state.priceCallData.physicalFormat.titer ===
        SpecificationMessages.TenPSix
      ) {
        setTiter(SpecificationMessages.powerof6)
      } else if (
        state.priceCallData.physicalFormat.titer ===
        SpecificationMessages.TenPSeven
      ) {
        setTiter(SpecificationMessages.powerof7)
      } else if (
        state.priceCallData.physicalFormat.titer ===
        SpecificationMessages.TenPEight
      ) {
        setTiter(SpecificationMessages.powerof8)
      } else if (
        state.priceCallData.physicalFormat.titer ===
        SpecificationMessages.TenPNine
      ) {
        setTiter(SpecificationMessages.powerof9)
      }
    }
  }

  const onPageLoadVolumeValues = (): void => {
    if (
      state.priceCallData.physicalFormat.volume !== '' &&
      state.priceCallData.physicalFormat.volume !== undefined
    ) {
      setVolFlag(true)
      if (
        state.priceCallData.physicalFormat.volume ===
        SpecificationMessages.dotTwo
      ) {
        setVolume(SpecificationMessages.pointTwo)
      } else if (
        state.priceCallData.physicalFormat.volume ===
        SpecificationMessages.dotOne
      ) {
        setVolume(SpecificationMessages.pointOne)
      } else if (
        state.priceCallData.physicalFormat.volume ===
        SpecificationMessages.oneVol
      ) {
        setVolume(SpecificationMessages.volOne)
      } else if (
        state.priceCallData.physicalFormat.volume ===
        SpecificationMessages.twoVol
      ) {
        setVolume(SpecificationMessages.volTwo)
      } else if (
        state.priceCallData.physicalFormat.volume ===
        SpecificationMessages.fiveVol
      ) {
        setVolume(SpecificationMessages.volFive)
      } else if (
        state.priceCallData.physicalFormat.volume ===
        SpecificationMessages.tenVol
      ) {
        setVolume(SpecificationMessages.volTen)
      }
    }
  }

  const onPageLoadVector = (): void => {
    if (
      state.priceCallData.physicalFormat.vector !== '' &&
      state.priceCallData.physicalFormat.vector !== undefined
    ) {
      setVectorFlag(true)
      if (
        state.priceCallData.physicalFormat.vector ===
        SpecificationMessages.pLKO1Value
      ) {
        setVector(SpecificationMessages.pLKO1)
      } else if (
        state.priceCallData.physicalFormat.vector ===
          SpecificationMessages.CMVNeoValue ||
        SpecificationMessages.CMVNeoValueDNA
      ) {
        setVector(SpecificationMessages.CMVNeo)
      } else if (
        state.priceCallData.physicalFormat.vector ===
          SpecificationMessages.hPGKNeoValue ||
        SpecificationMessages.hPGKNeoValueDNA
      ) {
        setVector(SpecificationMessages.hPGKNeo)
      } else if (
        state.priceCallData.physicalFormat.vector ===
          SpecificationMessages.CMVtGFPValue ||
        SpecificationMessages.CMVtGFPValueDNA
      ) {
        setVector(SpecificationMessages.CMVtGFP)
      } else if (
        state.priceCallData.physicalFormat.vector ===
          SpecificationMessages.hPGKNeoCMVtGFPValue ||
        SpecificationMessages.hPGKNeoCMVtGFPValueDNA
      ) {
        setVector(SpecificationMessages.hPGKNeoCMVtGFP)
      } else if (
        state.priceCallData.physicalFormat.vector ===
          SpecificationMessages.hPGKPuroCMVtGFPValue ||
        SpecificationMessages.hPGKPuroCMVtGFPValueDNA
      ) {
        setVector(SpecificationMessages.hPGKPuroCMVtGFP)
      }
    }
  }

  React.useEffect(() => {
    if (state.shrnaDropDownValue.physicalMaterial !== '') {
      setSelectedMaterial(state.shrnaDropDownValue.physicalMaterial)
    }
    if (
      state.shrnaDropDownValue.physicalMaterial === SpecificationMessages.VIRUS
    ) {
      onPageLoadTiterValues()
      onPageLoadVolumeValues()
      onPageLoadVector()
    }
    if (
      state.shrnaDropDownValue.physicalMaterial === SpecificationMessages.DNA
    ) {
      onPageLoadVector()
    }
    // TODO: need a test before we refactor these dependencies
  }, [])

  React.useEffect(() => {
    if (data !== undefined) {
      if (!data.getShrnaDropDownList?.serverError) {
        dispatch({
          type: 'UPDATE_DROPDOWN_RESPONSE',
          data: data.getShrnaDropDownList,
        })
      } else {
        dispatch({
          type: 'SET_PAGE_ERROR',
          data: SemiConfiguratorValues.InternalServerError,
        })
        window.scrollTo(0, 0)
      }
    }
    // TODO: need a test before we refactor these dependencies
  }, [data])

  interface DropDownData {
    label: string
    value: string
  }

  const dropDownData: DropDownData = {
    label: '',
    value: '',
  }

  const getData = (data) => {
    return data.map((item) => {
      const result = {}
      const keys = Object.keys(item)
      keys.forEach((o) => {
        if (item[o]) {
          result[o] = item[o]
        }
      })
      return result
    })
  }

  const dropdownType = (shrnaDropDownResponseList): DropDownData[] => {
    const dropDownList = [dropDownData]
    if (shrnaDropDownResponseList.physicalMaterial !== null) {
      const result = getData(shrnaDropDownResponseList.physicalMaterial)
      dropDownList.length = 0
      result.forEach((sc) => {
        delete sc.__typename
        dropDownData.label = Object.values(sc).toString()
        dropDownData.value = Object.keys(sc).toString()
        dropDownList.push({ ...dropDownData })
      })
    }
    return dropDownList
  }

  const getKey = (key) => {
    const data = getPhysicalMaterialKey.filter((item) => item.key === key)
    return data[0].value
  }

  const vectorDropdown = (shrnaDropDownResponseList): DropDownData[] => {
    const vectorList = [dropDownData]
    if (shrnaDropDownResponseList.vector !== null) {
      const result = getData(shrnaDropDownResponseList.vector)
      vectorList.length = 0
      result.forEach((sc) => {
        delete sc.__typename
        dropDownData.label = getKey(Object.values(sc).toString())
        dropDownData.value = Object.keys(sc).toString()
        vectorList.push({ ...dropDownData })
      })

      if (!vectorFlag) {
        const vectorValue: [any] = result[0]
        const vectorKey = Object.values(vectorValue)[0]
        const vectorData = Object.keys(vectorValue)[0]
        setVector(vectorData)
        state.specificationValues.vector = vectorKey
        state.priceCallData.physicalFormat.vector = vectorKey
      }
    }
    return vectorList
  }

  const titerDropdown = (shrnaDropDownResponseList): DropDownData[] => {
    const titerList = [dropDownData]

    if (shrnaDropDownResponseList.titer !== null) {
      const result = getData(shrnaDropDownResponseList.titer)
      titerList.length = 0
      result.forEach((sc) => {
        delete sc.__typename
        dropDownData.label = Object.values(sc).toString()
        dropDownData.value = Object.keys(sc).toString()
        titerList.push({ ...dropDownData })
      })

      if (!titerFlag && state.priceCallData.physicalFormat.titer === '') {
        const titerValue: [any] = result[0]
        setTiter(Object.keys(titerValue)[0])
        const titerData = Object.values(titerValue)[0]
        state.specificationValues.titer = titerData
        state.priceCallData.physicalFormat.titer = SpecificationMessages.TenPSix
      }
    }
    return titerList
  }

  const volumeDropdown = (shrnaDropDownResponseList): DropDownData[] => {
    const volumeList = [dropDownData]

    if (shrnaDropDownResponseList.volume !== null) {
      const result = getData(shrnaDropDownResponseList.volume)
      volumeList.length = 0
      result.forEach((sc) => {
        delete sc.__typename
        dropDownData.label = Object.values(sc).toString()
        dropDownData.value = Object.keys(sc).toString()
        volumeList.push({ ...dropDownData })
      })
      if (!volFlag) {
        const volValue: [any] = result[0]
        setVolume(Object.keys(volValue)[0])
        const volData = Object.values(volValue)[0]
        if (volData === SpecificationMessages.dotOneVal) {
          state.priceCallData.physicalFormat.volume =
            SpecificationMessages.dotOne
          state.specificationValues.volume = volData
        } else if (volData === SpecificationMessages.dotTwoVal) {
          state.priceCallData.physicalFormat.volume =
            SpecificationMessages.dotTwo
          state.specificationValues.volume = volData
        }
      }
    }
    return volumeList
  }

  const onMaterialChange = (event): void => {
    dispatch({
      type: 'CHECK_ACTIVE_LINK',
      data: {
        ...state.checkActiveLink,
        specificationLink: false,
      },
    })
    setLoader(true)
    if (state.stepNo !== 2) {
      setInitialState()
    }
    setOpen(false)
    setPhysicalMaterialNotSelected(false)
    const { value } = event.target
    setSelectedMaterial(value)
    state.shrnaDropDownValue.physicalMaterial = value
    state.priceCallData.country = props.productDetails.region
    const pricePhysicalFormat = { ...state.priceCallData.physicalFormat }
    pricePhysicalFormat.physicalMaterial = value
    pricePhysicalFormat.vector = SpecificationMessages.pLKO1Value
    setVector(SpecificationMessages.pLKO1)
    if (value === SpecificationMessages.GLYCEROL) {
      state.specificationValues.physicalMaterial =
        SpecificationMessages.GlycerolVal
      pricePhysicalFormat.titer = ''
      pricePhysicalFormat.volume = ''
      pricePhysicalFormat.vector = ''
    } else if (value === SpecificationMessages.DNA) {
      state.specificationValues.physicalMaterial = SpecificationMessages.DNAVal
      pricePhysicalFormat.titer = ''
      pricePhysicalFormat.volume = ''
    } else if (value === SpecificationMessages.VIRUS) {
      state.specificationValues.physicalMaterial =
        SpecificationMessages.VIRUSVal
      state.shrnaDropDownValue.titer = SpecificationMessages.TenPSix
      state.shrnaDropDownValue.volume = SpecificationMessages.dotTwo
    }
    dispatch({
      type: 'UPDATE_PRICE_CALL',
      data: { ...state.priceCallData, physicalFormat: pricePhysicalFormat },
    })
    dispatch({
      type: 'UPDATE_SPECIFICATION_VALUES',
      data: { ...state.specificationValues },
    })
    dispatch({
      type: 'UPDATE_DROP_DOWN',
      data: state.shrnaDropDownValue,
    })
    if (value !== SpecificationMessages.noSelection) {
      shrnaDropDownResult(state.shrnaDropDownValue)
    }
  }

  const onVectorChange = (event): void => {
    setLoader(false)
    if (state.stepNo !== 2) {
      setInitialState()
    }
    const { value } = event.target
    setVector(value)
    setVectorFlag(true)
    const pricePhysicalFormat = { ...state.priceCallData.physicalFormat }
    let vector = ''
    if (pricePhysicalFormat.physicalMaterial === 'DNA') {
      if (value === SpecificationMessages.pLKO1) {
        vector = SpecificationMessages.pLKO1Value
      } else if (value === SpecificationMessages.CMVNeo) {
        vector = SpecificationMessages.CMVNeoValueDNA
      } else if (value === SpecificationMessages.hPGKNeo) {
        vector = SpecificationMessages.hPGKNeoValueDNA
      } else if (value === SpecificationMessages.CMVtGFP) {
        vector = SpecificationMessages.CMVtGFPValueDNA
      } else if (value === SpecificationMessages.hPGKNeoCMVtGFP) {
        vector = SpecificationMessages.hPGKNeoCMVtGFPValueDNA
      } else if (value === SpecificationMessages.hPGKPuroCMVtGFP) {
        vector = SpecificationMessages.hPGKPuroCMVtGFPValueDNA
      }
    } else {
      if (value === SpecificationMessages.pLKO1) {
        vector = SpecificationMessages.pLKO1Value
      } else if (value === SpecificationMessages.CMVNeo) {
        vector = SpecificationMessages.CMVNeoValue
      } else if (value === SpecificationMessages.hPGKNeo) {
        vector = SpecificationMessages.hPGKNeoValue
      } else if (value === SpecificationMessages.CMVtGFP) {
        vector = SpecificationMessages.CMVtGFPValue
      } else if (value === SpecificationMessages.hPGKNeoCMVtGFP) {
        vector = SpecificationMessages.hPGKNeoCMVtGFPValue
      } else if (value === SpecificationMessages.hPGKPuroCMVtGFP) {
        vector = SpecificationMessages.hPGKPuroCMVtGFPValue
      }
    }

    pricePhysicalFormat.vector = vector
    dispatch({
      type: 'UPDATE_PRICE_CALL',
      data: { ...state.priceCallData, physicalFormat: pricePhysicalFormat },
    })
    state.specificationValues.vector = vector
    dispatch({
      type: 'UPDATE_SPECIFICATION_VALUES',
      data: { ...state.specificationValues },
    })
  }

  const onTiterChange = (event): void => {
    setLoader(false)
    if (state.stepNo !== 2) {
      setInitialState()
    }
    setVolFlag(false)
    setTiterFlag(true)
    const { value } = event.target
    const pricePhysicalFormat = { ...state.priceCallData.physicalFormat }
    let tit = ''
    let vol = ''
    let titVal = ''
    if (value === SpecificationMessages.powerof6) {
      tit = SpecificationMessages.TenPSix
      titVal = SpecificationMessages.TenPSixVal
      state.shrnaDropDownValue.volume = SpecificationMessages.dotTwo
    } else if (value === SpecificationMessages.powerof7) {
      tit = SpecificationMessages.TenPSeven
      titVal = SpecificationMessages.TenPSevenVal
      state.shrnaDropDownValue.volume = SpecificationMessages.dotTwo
    } else if (value === SpecificationMessages.powerof8) {
      tit = SpecificationMessages.TenPEight
      vol = SpecificationMessages.dotOne
      titVal = SpecificationMessages.TenPEightVal
      state.shrnaDropDownValue.volume = SpecificationMessages.dotOne
    } else if (value === SpecificationMessages.powerof9) {
      tit = SpecificationMessages.TenPNine
      titVal = SpecificationMessages.TenPNineVal
      state.shrnaDropDownValue.volume = SpecificationMessages.dotOne
    }
    pricePhysicalFormat.titer = tit
    pricePhysicalFormat.volume = vol
    dispatch({
      type: 'UPDATE_PRICE_CALL',
      data: { ...state.priceCallData, physicalFormat: pricePhysicalFormat },
    })

    state.specificationValues.titer = titVal
    dispatch({
      type: 'UPDATE_SPECIFICATION_VALUES',
      data: { ...state.specificationValues },
    })

    const requestData = { ...state.shrnaDropDownValue, titer: tit, volume: '' }

    setTiter(value)
    dispatch({
      type: 'UPDATE_DROP_DOWN',
      data: requestData,
    })

    shrnaDropDownResult(requestData)
  }

  const onVolumeChange = (event): void => {
    setLoader(false)
    if (state.stepNo !== 2) {
      setInitialState()
    }
    setVolFlag(true)
    setTiterFlag(true)
    const { value } = event.target
    const pricePhysicalFormat = { ...state.priceCallData.physicalFormat }

    let vol = ''
    let volVal = ''
    let tit = ''
    if (value === SpecificationMessages.pointTwo) {
      vol = SpecificationMessages.dotTwo
      volVal = SpecificationMessages.dotTwoVal
    } else if (value === SpecificationMessages.pointOne) {
      vol = SpecificationMessages.dotOne
      volVal = SpecificationMessages.dotOneVal
    } else if (value === SpecificationMessages.volOne) {
      vol = SpecificationMessages.oneVol
      volVal = SpecificationMessages.oneVolVal
    } else if (value === SpecificationMessages.volTwo) {
      vol = SpecificationMessages.twoVol
      volVal = SpecificationMessages.twoVolVal
    } else if (value === SpecificationMessages.volFive) {
      vol = SpecificationMessages.fiveVol
      volVal = SpecificationMessages.fiveVolVal
    } else if (value === SpecificationMessages.volTen) {
      vol = SpecificationMessages.tenVol
      volVal = SpecificationMessages.tenVolVal
    }
    pricePhysicalFormat.volume = vol
    pricePhysicalFormat.titer = state.priceCallData.physicalFormat.titer
    tit = state.priceCallData.physicalFormat.titer

    dispatch({
      type: 'UPDATE_PRICE_CALL',
      data: { ...state.priceCallData, physicalFormat: pricePhysicalFormat },
    })

    state.specificationValues.volume = volVal
    dispatch({
      type: 'UPDATE_SPECIFICATION_VALUES',
      data: { ...state.specificationValues },
    })

    const volumeData = { ...state.shrnaDropDownValue, volume: vol, titer: tit }

    setVolume(value)
    dispatch({
      type: 'UPDATE_DROP_DOWN',
      data: volumeData,
    })

    shrnaDropDownResult(volumeData)
  }

  const onClickNextButton = (): void => {
    if (state.stepNo === 2) {
      dispatch({ type: 'TRACE_ON_CLICK', data: false })
    }
    if (selectedMaterial === SpecificationMessages.noSelection) {
      setPhysicalMaterialNotSelected(true)
      setValidationMessage([intl.formatMessage(messages.CONF_REQUIRED_FIELD)])
      setOpen(true)
    }
    if (selectedMaterial !== SpecificationMessages.noSelection) {
      const updateCountryCode = {
        ...state.priceCallData,
        country: props.productDetails.region,
      }

      dispatch({
        type: 'UPDATE_PRICE_CALL',
        data: updateCountryCode,
      })
      semiShrnaPriceCall(updateCountryCode)
    }
  }

  return (
    <div>
      <ErrorAlert open={open} setOpen={setOpen} message={message} />
      <Grid container>
        <Grid item sm={6}>
          <h4 className={classes.subHead}>
            <FormattedMessage {...messages.CONF_SPECIFICATIONS_crisprgrna} />
          </h4>
        </Grid>
      </Grid>
      <div className={classes.contentWrapper}>
        <Box>
          <Formik
            initialValues={{
              materialName: (
                <FormattedMessage
                  {...messages.CONF_SPECIFICATION_PHYSICAL_MATERIAL_SYNTHETIC_RNA_crisprgrna}
                />
              ),
            }}
            onSubmit={(values, actions) => {
              setTimeout(() => {
                actions.setSubmitting(false)
              }, 1000)
            }}
          >
            {() => (
              <>
                <Grid
                  container
                  spacing={2}
                  className={classes.fieldLabelphyMat}
                >
                  <Grid item xs={4}>
                    <Field
                      name="details"
                      component={LiquidSelectAdapter}
                      label={
                        <FormattedMessage
                          {...messages.CONF_SPECIFICATION_PHYSICAL_MATERIAL}
                        />
                      }
                      onChange={onMaterialChange}
                      value={
                        selectedMaterial ===
                          SpecificationMessages.noSelection &&
                        state.shrnaDropDownValue.physicalMaterial !== ''
                          ? state.shrnaDropDownValue.physicalMaterial
                          : selectedMaterial
                      }
                      options={[...dropdownType(shrnaDropDownResponseList)]}
                      filled
                    />
                    {physicalMaterialNotSelected && (
                      <span>
                        <ValidationErrors errors={validationMessage} />
                      </span>
                    )}
                  </Grid>
                </Grid>
                {selectedMaterial === SpecificationMessages.DNA ? (
                  dropdownload && loader ? (
                    ''
                  ) : (
                    <>
                      <Grid
                        container
                        spacing={5}
                        className={classes.fieldLabel}
                        alignItems="flex-end"
                      >
                        <Grid item xs={6}>
                          <Field
                            name="vector"
                            component={LiquidSelectAdapter}
                            label={
                              <FormattedMessage
                                {...messages.CONF_DNA_SPECIFICATION_crispgrna}
                              />
                            }
                            value={vector}
                            onChange={onVectorChange}
                            className={classes.vectorLabel2}
                            options={[
                              ...vectorDropdown(shrnaDropDownResponseList),
                            ]}
                            filled
                          />
                          <span className={classes.vectorLabel}>
                            <FormattedMessage
                              {...messages.CONF_DNA_DROPDOWN_SPECIFICATION_crispgrna}
                            />
                          </span>
                          <Link
                            href="/technical-documents/technical-article/genomics/gene-expression-and-silencing/vector-map"
                            underline="hover"
                            target="_blank"
                          >
                            <span className={classes.linkStyle}>
                              <FormattedMessage
                                {...messages.CONF_DNA_LINK_SPECIFICATION_shrna}
                              />
                            </span>
                          </Link>
                        </Grid>
                      </Grid>
                    </>
                  )
                ) : selectedMaterial === SpecificationMessages.VIRUS ? (
                  dropdownload && loader ? (
                    ''
                  ) : (
                    <>
                      <Grid
                        container
                        spacing={5}
                        className={classes.fieldLabel}
                        alignItems="flex-end"
                      >
                        <Grid item xs={6}>
                          <Field
                            name="vector"
                            component={LiquidSelectAdapter}
                            label={
                              <FormattedMessage
                                {...messages.CONF_DNA_SPECIFICATION_crispgrna}
                              />
                            }
                            value={vector}
                            onChange={onVectorChange}
                            className={classes.vectorLabel2}
                            options={[
                              ...vectorDropdown(shrnaDropDownResponseList),
                            ]}
                            filled
                          />
                        </Grid>
                      </Grid>
                      <Grid
                        container
                        spacing={5}
                        className={classes.fieldLabel}
                        alignItems="flex-end"
                      >
                        <Grid item xs={4}>
                          <Field
                            name="titerField"
                            component={LiquidSelectAdapter}
                            label={
                              <span>
                                <FormattedMessage
                                  {...messages.CONF_SPEC_VIRAL_TITER}
                                />

                                <Tooltip
                                  title={
                                    <FormattedMessage
                                      {...messages.CONF_SANGER_TITER_TOOLTIP}
                                    />
                                  }
                                  classes={{
                                    tooltip: classes.InfoPopup,
                                    arrow: classes.arrow,
                                  }}
                                  arrow
                                  placement="bottom-end"
                                >
                                  <InfoIcon
                                    aria-label="info"
                                    className={classes.InfoIcon}
                                    color="primary"
                                  ></InfoIcon>
                                </Tooltip>
                              </span>
                            }
                            value={titer}
                            onChange={onTiterChange}
                            options={[
                              ...titerDropdown(shrnaDropDownResponseList),
                            ]}
                            filled
                          />
                        </Grid>
                      </Grid>

                      <Grid
                        container
                        spacing={5}
                        className={classes.fieldLabel}
                        alignItems="flex-end"
                      >
                        <Grid item xs={4}>
                          <Field
                            name="volume"
                            component={LiquidSelectAdapter}
                            label={
                              <span>
                                <FormattedMessage
                                  {...messages.CONF_VIRUS_VOLUME_SPECIFICATION_crispgrna}
                                />

                                <Tooltip
                                  title={
                                    <FormattedMessage
                                      {...messages.CONF_SANGER_VOLUME_TOOLTIP}
                                    />
                                  }
                                  classes={{
                                    tooltip: classes.InfoPopup,
                                    arrow: classes.arrow,
                                  }}
                                  arrow
                                  placement="bottom-end"
                                >
                                  <InfoIcon
                                    aria-label="info"
                                    className={classes.InfoIcon}
                                    color="primary"
                                  ></InfoIcon>
                                </Tooltip>
                              </span>
                            }
                            value={volume}
                            onChange={onVolumeChange}
                            options={[
                              ...volumeDropdown(shrnaDropDownResponseList),
                            ]}
                            filled
                          />
                        </Grid>
                      </Grid>
                    </>
                  )
                ) : (
                  ''
                )}
              </>
            )}
          </Formik>
        </Box>
      </div>
      <Grid container>
        <Grid item sm={6}>
          <Button
            color="primary"
            variant="contained"
            className={classes.nextButton}
            onClick={onClickNextButton}
          >
            <FormattedMessage {...messages.CONTINUE} />
          </Button>
        </Grid>
      </Grid>
      {((dropdownload &&
        state.stepNo === 2 &&
        !state.checkActiveLink.specificationLink) ||
        priceload) && <CircleLoader />}
    </div>
  )
}

export default Specifications
